import { useRef } from 'react';
import { useFeedbackDictation } from './useFeedbackDictation.js';
import { annotationKindLabel } from './feedbackTypes.js';

// Canonical element-feedback category vocabulary. Exported so the governed
// Feedback Queue triage surface (FeedbackQueuePage) shares one authoritative
// list instead of maintaining a divergent, wrong-cased copy (H3).
export const CATEGORY_OPTIONS = [
  { value: 'ux_confusion', label: 'UX confusion' },
  { value: 'workflow_gap', label: 'Workflow gap' },
  { value: 'data_quality', label: 'Data quality' },
  { value: 'content', label: 'Content' },
  { value: 'accessibility', label: 'Accessibility' },
];

export const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Nice to have' },
  { value: 'medium', label: 'Should fix' },
  { value: 'high', label: 'Important' },
  { value: 'critical', label: 'Blocking' },
];

const PANEL_SEVERITY_OPTIONS = [
  { value: 'low', label: 'Nice to have' },
  { value: 'high', label: 'Important' },
  { value: 'critical', label: 'Blocking' },
];

// W2 redesign: the panel leads with one plain question writing
// annotation.instruction — the single source of truth for the request text
// (the context menu's Note edits the same field). The old Description field
// is gone; legacy drafts that only carried feedbackDraft.description still
// display through the read fallback below and keep working in the queue,
// which prefers draft.description when present.
export function AnnotationDetailsPanel({
  annotation,
  actorUserId = null,
  onUpdate,
  onDelete,
  autoFocusNote = false,
  noteTestId = 'feedback-primary-note',
  dictateTestId = null,
  dictationStatusTestId = null,
}) {
  const draft = annotation?.feedbackDraft || {};
  const noteValue = annotation?.instruction || draft.description || '';
  const severityValue = draft.severity || null;
  const hasDetailContent = Boolean(draft.actualBehavior || draft.expectedBehavior);
  const markNumber = String(annotation?.id || '').replace(/^A-0*/, '') || '1';
  const kindLabel = annotationKindLabel(annotation?.type);
  const noteRef = useRef(null);
  const dictation = useFeedbackDictation({
    annotationId: annotation?.id || null,
    actorUserId,
    noteRef,
    noteValue,
    onNoteValueChange: (value) => {
      if (!annotation) return;
      onUpdate(annotation.id, { instruction: value });
    },
  });
  const dictationStatusMessage =
    dictation.status === 'listening'
      ? 'Listening…'
      : dictation.message || (!dictation.canDictate ? dictation.availabilityMessage : '');

  if (!annotation) return null;

  function updateDraft(field, value, extras = {}) {
    onUpdate(annotation.id, {
      ...extras,
      feedbackDraft: {
        ...draft,
        [field]: value,
      },
    });
  }

  return (
    <div className="feedback-details-panel" data-testid="feedback-details-panel">
      <div className="feedback-panel-head panel-head">
        <span className="feedback-panel-mark-badge">{markNumber}</span>
        <span className="feedback-panel-mark-kind">
          MARK {markNumber} · {kindLabel.toUpperCase()}
        </span>
        <button
          type="button"
          className="feedback-panel-delete"
          data-testid="feedback-panel-delete"
          aria-label="Delete this mark"
          onClick={onDelete}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 6h18" />
            <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
            <path d="M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14" />
          </svg>
        </button>
      </div>

      <label className="feedback-field feedback-primary-question">
        <span className="feedback-primary-question-label">What would you like changed?</span>
        <textarea
          key={annotation.id}
          ref={noteRef}
          data-testid={noteTestId}
          aria-label="What would you like changed?"
          placeholder="Say it in your own words — e.g., “Make this button say ‘Submit request’ instead.”"
          value={dictation.displayValue ?? noteValue}
          readOnly={dictation.status === 'listening'}
          onChange={(event) => onUpdate(annotation.id, { instruction: event.target.value })}
          rows={4}
          autoFocus={autoFocusNote}
        />
      </label>

      <div className="feedback-dictation-row">
        <button
          type="button"
          className="btn ghost sm"
          data-testid={dictateTestId || undefined}
          onClick={() =>
            dictation.status === 'listening'
              ? dictation.stopDictation()
              : dictation.startDictation()
          }
          disabled={dictation.status !== 'listening' && !dictation.canDictate}
        >
          {dictation.status === 'listening' ? 'Stop dictation' : 'Dictate feedback'}
        </button>
        <span
          className={`feedback-dictation-status ${dictation.status}`.trim()}
          data-testid={dictationStatusTestId || undefined}
          role="status"
          aria-live="polite"
        >
          {dictationStatusMessage}
        </span>
      </div>

      {dictation.consentRequired ? (
        <div
          className="feedback-dictation-notice"
          role="dialog"
          aria-label="Voice dictation privacy notice"
          aria-modal="false"
        >
          <strong>Before you use voice dictation</strong>
          <p>
            Your browser may send microphone audio to its speech service. DHA PRIME stores only
            the text added to this feedback, not audio.
          </p>
          <div className="feedback-dictation-notice-actions">
            <button type="button" className="btn primary sm" onClick={dictation.acknowledgeConsent}>
              Continue
            </button>
            <button type="button" className="btn ghost sm" onClick={dictation.cancelConsent}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      <div className="feedback-field">
        <span className="feedback-severity-label">
          How important is this to you? <em>(optional)</em>
        </span>
        <div className="feedback-severity-chips" role="group" aria-label="Severity">
          {PANEL_SEVERITY_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`feedback-severity-chip severity-${option.value} ${
                severityValue === option.value ? 'active' : ''
              }`}
              data-testid={`feedback-severity-${option.value}`}
              aria-pressed={severityValue === option.value}
              onClick={() =>
                updateDraft('severity', severityValue === option.value ? null : option.value)
              }
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <details
        className="feedback-details-more"
        data-testid="feedback-more-details"
        open={hasDetailContent || undefined}
      >
        <summary>Add more detail (optional)</summary>

        <label className="feedback-field">
          <span>What happens today?</span>
          <textarea
            aria-label="Actual behavior"
            placeholder="e.g., The SLA column shows '1d overdue' but doesn't say which day it was due."
            value={draft.actualBehavior || ''}
            onChange={(event) => updateDraft('actualBehavior', event.target.value)}
            rows={3}
          />
        </label>

        <label className="feedback-field">
          <span>What should happen instead?</span>
          <textarea
            aria-label="Expected behavior"
            placeholder="e.g., Show the actual due date on hover."
            value={draft.expectedBehavior || ''}
            onChange={(event) => updateDraft('expectedBehavior', event.target.value)}
            rows={3}
          />
        </label>
      </details>
    </div>
  );
}
